// screens/Login/index.jsx
import React, { useState, useCallback, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
// Import existing components
import LoadingSpinner from '../../components/LoadingSpinner.js';
// Import new components
import LoginContainer from '../../components/Login/LoginContainer.jsx';
import LoginHeader from '../../components/Login/LoginHeader.jsx';
import LoginError from '../../components/Login/LoginError.jsx';
import PhoneInput from '../../components/Login/PhoneInput.jsx';
import PasswordInput from '../../components/Login/PasswordInput.jsx';
import ForgotPasswordButton from '../../components/Login/ForgotPasswordButton.jsx';
import LoginButton from '../../components/Login/LoginButton.jsx';
// Import enhanced auth hook and 2FA service
import auth2FAService, { AuthError } from '../../services/auth/auth2FAService';
import { usePasswordReset } from '../../hooks/usePasswordReset.js';
import { useLanguage } from '../../i18n/LanguageContext.jsx';
import RawText from '../../i18n/RawText.jsx';

const ROLE_LABELS = {
  admin: 'Administrator',
  economist: 'Economist',
  dispatcher: 'Dispecer',
  driver: 'Șofer',
  fleet_manager: 'Fleet manager',
  expeditor: 'Expeditor'
};

const LoginScreen = ({ onLogin }) => {
  const { t } = useLanguage();
  const [formData, setFormData] = useState({
    phone_number: '+40 ',
    password: ''
  });
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [multipleAccounts, setMultipleAccounts] = useState([]);
  const [retryAfterSeconds, setRetryAfterSeconds] = useState(0);
  const navigate = useNavigate();
  const location = useLocation();

  const { isResetting } = usePasswordReset();

  useEffect(() => {
    const reason = new URLSearchParams(location.search).get('reason');
    const message = location.state?.sessionMessage
      || (reason === 'session-revoked' ? 'Sesiunea a expirat sau a fost revocată.' : '')
      || (reason === 'tenant-conflict' ? 'Sesiunea nu este validă pentru firma curentă.' : '');
    if (message) setError(message);
  }, [location.search, location.state]);

  useEffect(() => {
    if (retryAfterSeconds <= 0) return undefined;
    const timer = setTimeout(() => setRetryAfterSeconds((seconds) => Math.max(0, seconds - 1)), 1000);
    return () => clearTimeout(timer);
  }, [retryAfterSeconds]);

  // Update form field
  const updateField = useCallback((field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    setError('');
    setMultipleAccounts([]);
  }, []);

  // Handle login
  const handleLogin = useCallback(async (selectedRole = null) => {
    if (retryAfterSeconds > 0) return;
    if (!formData.phone_number || formData.phone_number === '+40 ') {
      setError('Introdu numărul de telefon asociat contului tău.');
      return;
    }

    if (!formData.password) {
      setError('Introdu parola contului tău.');
      return;
    }

    try {
      setError('');
      setMultipleAccounts([]);
      setIsLoading(true);

      const result = await auth2FAService.login(formData.phone_number, formData.password, selectedRole);

      if (result.requires2FA) {
        // Navigate to 2FA verification screen
        navigate('/2fa-verification');
      } else {
        // Regular login without 2FA
        if (onLogin) {
          onLogin(result.token);
        }
        // Navigate to main page
        window.location.href = '/main';
      }

    } catch (loginError) {

      // Set user-friendly error messages based on new error codes
      if (loginError instanceof AuthError) {
        switch (loginError.errorCode) {
          case 'INVALID_CREDENTIALS':
            setError('Nu am găsit un cont cu aceste date. Verifică numărul de telefon și parola.');
            break;
          case 'RATE_LIMIT':
            setRetryAfterSeconds(loginError.retryAfter || 60);
            setError(`Prea multe încercări. Reîncearcă în ${loginError.retryAfter || 60} secunde.`);
            break;
          case 'NO_EMAIL_FOR_2FA':
            setError('Contul tău are 2FA activat dar nu are email setat. Contactează administratorul.');
            break;
          case 'MULTIPLE_ACCOUNTS':
            setError('Alege rolul cu care vrei să te autentifici pentru acest număr.');
            setMultipleAccounts(loginError.accounts || []);
            break;
          case 'NETWORK_ERROR':
            setError('Nu putem stabili conexiunea cu serverul. Verifică internetul și încearcă din nou.');
            break;
          case 'SERVER_UNAVAILABLE':
            setError('Serverul nu răspunde în acest moment. Te rugăm să încerci din nou mai târziu.');
            break;
          default:
            setError(loginError.message || 'A apărut o problemă neașteptată. Încearcă din nou.');
        }
      } else if (loginError?.name === 'TypeError' || loginError?.message === 'Failed to fetch') {
        setError('Nu putem stabili conexiunea cu serverul. Verifică internetul și încearcă din nou.');
      } else {
        setError('A apărut o problemă neașteptată. Dacă persistă, contactează echipa de suport.');
      }
    } finally {
      setIsLoading(false);
    }
  }, [formData, navigate, onLogin, retryAfterSeconds]);

  // Handle key press
  const handleKeyPress = useCallback((e) => {
    if (e.key === 'Enter') {
      handleLogin();
    }
  }, [handleLogin]);

  // Handle forgot password
  const handleForgotPassword = () => {
    navigate('/forgot-password');
  };

  // Handle registration

  // Handle expeditor navigation
  const handleExpeditorClick = () => {
    navigate('/expeditor', { state: { type: 'expeditor' } });
  };

  // Handle destinatar navigation
  const handleDestinatarClick = () => {
    navigate('/expeditor', { state: { type: 'destinatar' } });
  };

  // Handle semnatar general navigation
  const handleSemnatarGeneralClick = () => {
    navigate('/expeditor', { state: { type: 'semnatar_general' } });
  };

  // Show full-screen loading spinner during login process
  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center">
        <LoadingSpinner showText={false} />
        <p className="mt-4 text-neutral-600 text-sm font-medium">Se autentifică...</p>
      </div>
    );
  }

  return (
    <LoginContainer>
      {/* Header */}
      <LoginHeader />

      {/* Error Display */}
      <LoginError error={error} />

      {multipleAccounts.length > 0 && (
        <div className="mb-6 rounded-[24px] border border-white/70 bg-white/80 p-4 shadow-[8px_8px_18px_rgba(0,0,0,0.06),-8px_-8px_18px_rgba(255,255,255,0.95)]">
          <p className="mb-3 text-sm font-semibold text-slate-700">
            Sunt mai multe conturi active pe acest număr.
          </p>
          <div className="space-y-2">
            {multipleAccounts.map((account) => (
              <button
                key={`${account.role}-${account.id}`}
                type="button"
                onClick={() => handleLogin(account.role)}
                className="flex w-full items-center justify-between rounded-[18px] border border-slate-200 bg-white px-4 py-3 text-left shadow-[inset_2px_2px_4px_rgba(0,0,0,0.02),inset_-2px_-2px_4px_rgba(255,255,255,0.9)] transition hover:border-slate-300"
              >
                <span>
                  <span className="block text-sm font-bold text-slate-900">
                    {ROLE_LABELS[account.role] || account.role}
                  </span>
                  <span className="block text-xs text-slate-500">
                    {account.name ? <RawText>{account.name}</RawText> : 'Cont'}
                    {account.company ? <> · <RawText>{account.company}</RawText></> : null}
                  </span>
                </span>
                <span className="text-xs font-semibold uppercase tracking-wider text-blue-600">
                  Intră
                </span>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Phone Input */}
      <PhoneInput
        value={formData.phone_number}
        onChange={updateField}
        onKeyPress={handleKeyPress}
      />

      {/* Password Input */}
      <PasswordInput
        value={formData.password}
        onChange={updateField}
        onKeyPress={handleKeyPress}
      />

      {/* Forgot Password */}
      <ForgotPasswordButton
        onClick={handleForgotPassword}
        isLoading={isResetting}
      />

      {/* Login Button */}
      <LoginButton
        onClick={() => handleLogin()}
        isLoading={isLoading}
        disabled={retryAfterSeconds > 0}
        label={retryAfterSeconds > 0 ? `${t('common.retry').toUpperCase()} ${retryAfterSeconds}s` : t('login.submit')}
      />

      {/* Expeditor/Destinatar/Semnatar General Buttons */}
      <div className="mt-6 space-y-3">
        <div className="text-center">
          <p className="text-sm text-neutral-500 font-medium mb-3">{t('login.alternateAccess')}</p>
        </div>

        <button
          onClick={handleExpeditorClick}
          className="relative w-full h-14 group overflow-hidden rounded-[24px] bg-gradient-to-br from-white/95 to-white/80 backdrop-blur-xl shadow-[0_8px_32px_rgba(0,0,0,0.06),8px_8px_16px_rgba(0,0,0,0.05),-8px_-8px_16px_rgba(255,255,255,1)] hover:shadow-[0_12px_36px_rgba(0,0,0,0.08),10px_10px_20px_rgba(0,0,0,0.06),-10px_-10px_20px_rgba(255,255,255,1)] border border-white/70 hover:-translate-y-0.5 transition-all duration-300"
        >
          <div className="absolute inset-0 bg-gradient-to-br from-blue-50/30 to-transparent" />
          <div className="absolute inset-0 rounded-[24px] shadow-[inset_0_1px_3px_rgba(255,255,255,0.9),inset_0_-1px_3px_rgba(0,0,0,0.05)]" />
          <div className="absolute inset-0 opacity-0 group-hover:opacity-100 bg-gradient-to-t from-blue-100/20 to-transparent transition-opacity duration-300" />
          <span className="relative z-10 text-blue-600 text-sm font-bold tracking-wide">{t('login.expeditor')}</span>
        </button>

        <button
          onClick={handleDestinatarClick}
          className="relative w-full h-14 group overflow-hidden rounded-[24px] bg-gradient-to-br from-white/95 to-white/80 backdrop-blur-xl shadow-[0_8px_32px_rgba(0,0,0,0.06),8px_8px_16px_rgba(0,0,0,0.05),-8px_-8px_16px_rgba(255,255,255,1)] hover:shadow-[0_12px_36px_rgba(0,0,0,0.08),10px_10px_20px_rgba(0,0,0,0.06),-10px_-10px_20px_rgba(255,255,255,1)] border border-white/70 hover:-translate-y-0.5 transition-all duration-300"
        >
          <div className="absolute inset-0 bg-gradient-to-br from-green-50/30 to-transparent" />
          <div className="absolute inset-0 rounded-[24px] shadow-[inset_0_1px_3px_rgba(255,255,255,0.9),inset_0_-1px_3px_rgba(0,0,0,0.05)]" />
          <div className="absolute inset-0 opacity-0 group-hover:opacity-100 bg-gradient-to-t from-green-100/20 to-transparent transition-opacity duration-300" />
          <span className="relative z-10 text-green-600 text-sm font-bold tracking-wide">{t('login.recipient')}</span>
        </button>

        <button
          onClick={handleSemnatarGeneralClick}
          className="relative w-full h-14 group overflow-hidden rounded-[24px] bg-gradient-to-br from-white/95 to-white/80 backdrop-blur-xl shadow-[0_8px_32px_rgba(0,0,0,0.06),8px_8px_16px_rgba(0,0,0,0.05),-8px_-8px_16px_rgba(255,255,255,1)] hover:shadow-[0_12px_36px_rgba(0,0,0,0.08),10px_10px_20px_rgba(0,0,0,0.06),-10px_-10px_20px_rgba(255,255,255,1)] border border-white/70 hover:-translate-y-0.5 transition-all duration-300"
        >
          <div className="absolute inset-0 bg-gradient-to-br from-purple-50/30 to-transparent" />
          <div className="absolute inset-0 rounded-[24px] shadow-[inset_0_1px_3px_rgba(255,255,255,0.9),inset_0_-1px_3px_rgba(0,0,0,0.05)]" />
          <div className="absolute inset-0 opacity-0 group-hover:opacity-100 bg-gradient-to-t from-purple-100/20 to-transparent transition-opacity duration-300" />
          <span className="relative z-10 text-purple-600 text-sm font-bold tracking-wide">{t('login.signer')}</span>
        </button>
      </div>

    </LoginContainer>
  );
};

export default LoginScreen;
